"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useKonamiCode } from "@/hooks/useKonamiCode";
import { createFullscreen, type FullscreenMode } from "./fullscreen";
import { loadLanguage, saveLanguage, uiText, type GameLanguage, type UiTextId } from "./language";
import { PAUSE_3D } from "@/components/3D/Utils/postFxTuning";
import { bootChirp, audioContext, isMuted, setMuted, subscribeMuted, suspendAudio, unlockAudio } from "./sound";
type GameModule = typeof import("./game");
type Phase = "idle" | "boot" | "error" | "game";
const BOOT_LINES: UiTextId[] = ["ui.cheatCodeAccepted", "ui.accessingMassifDeepSpaceNetwork", "ui.640kMemoryOk", "ui.loadingScrumExe"];

// The game chunk (Phaser + content) is only requested when the game opens.
let loading: Promise<GameModule> | null = null;
const loadGame = () => loading ??= import("./game").catch(e => {
  loading = null; // allow RETRY
  throw e;
});
export default function ScrumNebulaLauncher(t0) {
  const $ = _c(56);
  const {
    siteLanguage
  } = t0;
  const [language, setLanguage] = useState(siteLanguage === "de" ? "de" : "en");
  const [atTitle, setAtTitle] = useState(false);
  let t1;
  if ($[0] !== language) {
    t1 = text => uiText(language, text);
    $[0] = language;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const [phase, setPhase] = useState("idle");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [lines, setLines] = useState(t2);
  const [artReady, setArtReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fullscreenMode, setFullscreenMode] = useState("off");
  const [fullscreenSupported, setFullscreenSupported] = useState(false);
  const fullscreen = useRef(null);
  const overlay = useRef(null);
  const host = useRef(null);
  const game = useRef(null);
  const stopBoot = useRef(null);
  const active = phase !== "idle";
  const muted = useSyncExternalStore(subscribeMuted, isMuted, _temp);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      stopBoot.current?.();
      stopBoot.current = null;
      suspendAudio();
      fullscreen.current?.exit();
      setFullscreenMode("off");
      setPhase("idle");
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const close = t3;
  let t4;
  if ($[4] !== siteLanguage) {
    t4 = () => {
      if (overlay.current) {
        return;
      }
      const ctx = unlockAudio();
      if (ctx) {
        stopBoot.current = bootChirp(ctx);
      }
      setLines([]);
      setArtReady(false);
      setProgress(0);
      setAtTitle(false);
      setLanguage(loadLanguage(siteLanguage));
      setPhase(_temp2);
    };
    $[4] = siteLanguage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const open = t4;
  useKonamiCode(open);
  let t5;
  let t6;
  if ($[6] !== active) {
    t5 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const control = createFullscreen(overlay.current, setFullscreenMode);
      fullscreen.current = control;
      setFullscreenSupported(control.supported);
      return () => {
        control.dispose();
        fullscreen.current = null;
      };
    };
    t6 = [active];
    $[6] = active;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== open) {
    t7 = () => {
      const query = new URLSearchParams(window.location.search);
      if (query.has("konami") || process.env.NODE_ENV === "development" && (query.has("scrum-preview") || query.has("scrum-room"))) {
        open();
      }
    };
    t8 = [open];
    $[9] = open;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== language || $[13] !== phase) {
    t9 = () => {
      if (phase !== "boot") {
        return;
      }
      let alive = true;
      const visibility = _temp5;
      document.addEventListener("visibilitychange", visibility);
      if (document.hidden) {
        visibility();
      }
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const step = reduced ? 150 : 600;
      const timers = BOOT_LINES.map((_, i) => window.setTimeout(() => setLines(BOOT_LINES.slice(0, i + 1).map(line => uiText(language, line))), i * step));
      Promise.all([loadGame(), new Promise(r => window.setTimeout(r, BOOT_LINES.length * step))]).then(t11 => {
        const [mod] = t11;
        if (!alive) {
          return;
        }
        game.current = mod;
        setPhase("game");
      }).catch(() => {
        if (alive) {
          suspendAudio();
          setPhase("error");
        }
      });
      return () => {
        alive = false;
        timers.forEach(clearTimeout);
        document.removeEventListener("visibilitychange", visibility);
        stopBoot.current?.();
        stopBoot.current = null;
      };
    };
    t10 = [phase, language];
    $[12] = language;
    $[13] = phase;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== language || $[17] !== phase) {
    t11 = () => {
      if (phase !== "game" || !host.current || !game.current) {
        return;
      }
      host.current.focus();
      return game.current.startGame(host.current, {
        language,
        onTitle: setAtTitle,
        onExit: close,
        onProgress: setProgress,
        onReady: () => setArtReady(true),
        onError: () => setPhase("error")
      });
    };
    t12 = [phase, close, language];
    $[16] = language;
    $[17] = phase;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== active) {
    t13 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const self = overlay.current;
      const prevFocus = document.activeElement as HTMLElement | null;
      const siblings = [...document.body.children].filter(el => el instanceof HTMLElement && el !== self && !el.inert);
      siblings.forEach(_temp6);
      const root = document.documentElement;
      const overflow = root.style.overflow;
      root.style.overflow = "hidden";
      PAUSE_3D.value = true;
      const onKey = e => {
        if (e.key !== "Escape") {
          return;
        }
        e.stopPropagation();
        if (fullscreen.current && fullscreen.current.mode !== "off") {
          e.preventDefault();
          fullscreen.current?.exit();
          return;
        }
        close();
      };
      window.addEventListener("keydown", onKey, true);
      return () => {
        siblings.forEach(_temp7);
        if (root.style.overflow === "hidden") {
          root.style.overflow = overflow;
        }
        PAUSE_3D.value = false;
        window.removeEventListener("keydown", onKey, true);
        prevFocus?.focus();
      };
    };
    t14 = [active, close];
    $[20] = active;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  if (!active) {
    return null;
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      paddingTop: "env(safe-area-inset-top)",
      paddingRight: "env(safe-area-inset-right)",
      paddingBottom: "env(safe-area-inset-bottom)",
      paddingLeft: "env(safe-area-inset-left)"
    };
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== lines || $[25] !== phase || $[26] !== t) {
    t16 = phase === "game" ? <div ref={host} tabIndex={-1} className="flex min-h-0 flex-1 items-center justify-center outline-none" /> : <pre className="overflow-auto whitespace-pre-wrap break-words p-4 text-xs leading-loose sm:p-6 sm:text-sm">{lines.join("\n")}{phase === "error" && `\n\n${t("ui.diskReadError")}\n${t("ui.abortRetryFail")}`}</pre>;
    $[24] = lines;
    $[25] = phase;
    $[26] = t;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== artReady || $[29] !== phase || $[30] !== progress || $[31] !== t) {
    t17 = phase === "game" && !artReady && <div className="absolute inset-0 flex items-center justify-center bg-black"><p role="status" className="px-4 text-center text-sm">{t("ui.loadingSectorArt")} {Math.round(progress * 100)}%</p></div>;
    $[28] = artReady;
    $[29] = phase;
    $[30] = progress;
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== phase || $[34] !== t) {
    t18 = phase === "error" && <div className="flex gap-4 px-6"><button type="button" onClick={() => {
        const ctx_1 = unlockAudio();
        if (ctx_1) {
          stopBoot.current = bootChirp(ctx_1);
        }
        setLines([]);
        setArtReady(false);
        setProgress(0);
        setPhase("boot");
      }} className="underline">{t("ui.retry")}</button><button type="button" onClick={close} className="underline">{t("ui.abort")}</button></div>;
    $[33] = phase;
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== fullscreenMode) {
    t19 = fullscreenMode !== "off" ? {
      top: "max(0.75rem, env(safe-area-inset-top))",
      right: "max(0.75rem, env(safe-area-inset-right))"
    } : undefined;
    $[36] = fullscreenMode;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = fullscreenMode !== "off" ? "absolute z-10" : "relative z-10 order-first grid shrink-0 grid-cols-2 gap-2 p-3 text-xs uppercase tracking-wide sm:flex sm:items-center sm:justify-end sm:gap-4 sm:px-4";
  let t21;
  if ($[38] !== atTitle || $[39] !== fullscreenMode || $[40] !== fullscreenSupported || $[41] !== language || $[42] !== muted || $[43] !== phase || $[44] !== t) {
    t21 = fullscreenMode !== "off" ? <button type="button" aria-label={t(fullscreenMode === "native" ? "ui.exitFullscreen" : "ui.showControls")} title={t(fullscreenMode === "native" ? "ui.exitFullscreen" : "ui.showControls")} onClick={() => {
      fullscreen.current?.exit();
    }} className="flex size-11 items-center justify-center bg-black/70 text-white hover:bg-black"><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 9h5V4m11 5h-5V4M4 15h5v5m11-5h-5v5" /></svg></button> : <>{(phase !== "game" || atTitle) && <label className="flex min-h-11 items-center justify-center gap-2 bg-white/5 px-3 sm:bg-transparent"><span className="sr-only sm:not-sr-only">{t("ui.language")}</span><select aria-label={t("ui.language")} value={language} onKeyDown={_temp11} onChange={event_3 => {
          const next = event_3.target.value === "de" ? "de" : "en";
          saveLanguage(next);
          setLines([]);
          setArtReady(false);
          setLanguage(next);
        }} className="min-h-11 cursor-pointer bg-transparent text-base hover:text-white sm:text-xs"><option value="en" lang="en">English</option><option value="de" lang="de">Deutsch</option></select></label>}<button type="button" aria-pressed={muted} onClick={() => setMuted(!muted)} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t(muted ? "ui.soundOff" : "ui.soundOn")}<span className="hidden sm:pointer-fine:inline"> [M]</span></button><button type="button" onClick={() => {
        fullscreen.current?.enter();
      }} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t(fullscreenSupported ? "ui.fullscreen" : "ui.hideControls")}</button><button type="button" onClick={close} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t("ui.exit")}<span className="hidden sm:pointer-fine:inline"> [Esc]</span></button></>;
    $[38] = atTitle;
    $[39] = fullscreenMode;
    $[40] = fullscreenSupported;
    $[41] = language;
    $[42] = muted;
    $[43] = phase;
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== t21) {
    t22 = <div onMouseDown={_temp8} onTouchStart={_temp9} onKeyDown={_temp10} style={t19} className={t20}>{t21}</div>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== language || $[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t22) {
    t23 = <div ref={overlay} role="dialog" aria-modal="true" aria-label="The Secret of the SCRUM Nebula" lang={language} style={t15} className="fixed inset-x-0 top-0 z-[10000] flex h-dvh flex-col bg-black font-secondary text-[#c0c0c0]">{t16}{t17}{t18}{t22}</div>;
    $[50] = language;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  return t23;
}
function _temp11(event_2) {
  return event_2.stopPropagation();
}
function _temp10(event_1) {
  if (event_1.key === " " || event_1.key === "Enter") {
    event_1.stopPropagation();
  }
}
function _temp9(event_0) {
  return event_0.stopPropagation();
}
function _temp8(event) {
  return event.stopPropagation();
}
function _temp7(el_1) {
  return el_1.inert = false;
}
function _temp6(el_0) {
  return el_0.inert = true;
}
function _temp5() {
  const ctx_0 = audioContext();
  if (!ctx_0) {
    return;
  }
  if (document.hidden) {
    ctx_0.suspend().catch(_temp3);
  } else {
    ctx_0.resume().catch(_temp4);
  }
}
function _temp4() {}
function _temp3() {}
function _temp2(p) {
  return p === "idle" ? "boot" : p;
}
function _temp() {
  return false;
}